import { Loader2 } from "lucide-react";
import { FieldArrayWithId, UseFormReturn } from "react-hook-form";
import { Form } from "../../../../components/ui/common/form";
import { SOURCE_SECTION_CONFIGS } from "../constants/facilityDrawer.constants";
import { FacilityDraftFormValues, SourceCollectionKey, UnitOption } from "../types/facilityDrawer.types";
import EnergySection from "./EnergySection";
import FacilityInfoFields from "./FacilityInfoFields";
import FacilitySectionCard from "./FacilitySectionCard";
import VolumeSourceSection from "./VolumeSourceSection";

interface CountryOption {
  id: string;
  title: string;
  value: string;
}

interface FacilityDrawerFormBodyProps {
  form: UseFormReturn<FacilityDraftFormValues>;
  isOptionsLoading: boolean;
  countryOptions: CountryOption[];
  isCountriesLoading: boolean;
  energyOptions: UnitOption[];
  sourceOptionsFor: (key: SourceCollectionKey) => UnitOption[];
  energyFields: FieldArrayWithId<FacilityDraftFormValues, "energy", "id">[];
  sourceFields: Record<SourceCollectionKey, FieldArrayWithId<FacilityDraftFormValues, SourceCollectionKey, "id">[]>;
  distributionErrors: Record<string, string>;
  onAddEnergyRecord: () => void;
  onAddSourceRecord: (key: SourceCollectionKey) => void;
  onRemoveEnergyRecord: (index: number) => void;
  onRemoveSourceRecord: (key: SourceCollectionKey, index: number) => void;
}

const FacilityDrawerFormBody = ({
  form,
  isOptionsLoading,
  countryOptions,
  isCountriesLoading,
  energyOptions,
  sourceOptionsFor,
  energyFields,
  sourceFields,
  distributionErrors,
  onAddEnergyRecord,
  onAddSourceRecord,
  onRemoveEnergyRecord,
  onRemoveSourceRecord
}: FacilityDrawerFormBodyProps) => {
  if (isOptionsLoading) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-py-12" aria-busy="true" aria-live="polite">
        <Loader2 className="tw-h-8 tw-w-8 tw-animate-spin tw-text-muted-foreground" />
      </div>
    );
  }

  return (
    <Form {...form}>
      <FacilitySectionCard label="Facility info">
        <FacilityInfoFields
          control={form.control}
          countryOptions={countryOptions}
          isCountriesLoading={isCountriesLoading}
        />
      </FacilitySectionCard>

      <EnergySection
        control={form.control}
        fields={energyFields}
        energyOptions={energyOptions}
        distributionErrors={distributionErrors}
        onAdd={onAddEnergyRecord}
        onRemove={onRemoveEnergyRecord}
      />

      {SOURCE_SECTION_CONFIGS.map(config => (
        <VolumeSourceSection
          key={config.key}
          control={form.control}
          config={config}
          fields={sourceFields[config.key]}
          sourceOptions={sourceOptionsFor(config.key)}
          onAdd={() => onAddSourceRecord(config.key)}
          onRemove={index => onRemoveSourceRecord(config.key, index)}
        />
      ))}
    </Form>
  );
};

export default FacilityDrawerFormBody;
